import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';

import { informacionVuelta, limpiarVuelta } from '../../actions/vueltas';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

const tiposVuelta = [
    { value: 0, label: 'Cajero' },
    { value: 1, label: 'Bateador' },
    { value: 2, label: 'Promotor' },
    { value: 3, label: 'Freidor' },
    { value: 4, label: 'Vendedor ambulante' },
    { value: 5, label: 'Administrador' }
];

const dataTableProps = {
    pagination: true,
    paginationPerPage: 20,
    paginationComponentOptions: { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' },
    highlightOnHover: true
};

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const InformacionVuelta = () => {

    const dispatch = useDispatch();
    const { id } = useParams();

    const informacion = useSelector(state => state.vueltas.informacion);

    useEffect(() => {
        if (id) {
            dispatch(informacionVuelta(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Ventas ambulantes', path: '/vueltas', activo: false },
                { nombre: 'Información de venta', path: `/vueltas/informacion/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarVuelta());
        };
    }, [id]);

    const columnsProductos = [
        {
            name: 'Producto',
            sortable: true,
            selector: row => row.producto?.nombre || '-',
            grow: 2
        },
        {
            name: 'Cantidad',
            sortable: true,
            selector: row => row.cantidad ?? '-'
        },
        {
            name: 'Precio unitario',
            sortable: true,
            selector: row => row.precio != null ? formatNumber(row.precio, 2, '$', '') : '-'
        },
        {
            name: 'Extras',
            sortable: false,
            selector: row => row.extras?.length
                ? row.extras.map(e => e.nombre).join(', ')
                : '-',
            grow: 2
        },
        {
            name: 'Subtotal',
            sortable: true,
            selector: row => (row.precio != null && row.cantidad != null)
                ? formatNumber(row.precio * row.cantidad, 2, '$', '')
                : '-'
        }
    ];

    const columnsEmpleados = [
        {
            name: 'Empleado',
            sortable: true,
            selector: row => formatNombre(row.empleado),
            grow: 2
        },
        {
            name: 'Tipo',
            sortable: true,
            selector: row => {
                const tipo = tiposVuelta.find(t => t.value == row.tipo);
                return tipo?.label || '-';
            }
        },
        {
            name: 'Comisión',
            sortable: true,
            selector: row => row.total_comision != null ? formatNumber(row.total_comision, 2, '$', '') : '-'
        }
    ];

    return (
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-redo text-primary fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">
                            {informacion?.folio ? `Venta ambulante ${informacion.folio}` : 'Información de venta ambulante'}
                        </h4>
                        <small className="text-muted">Detalles completos de la venta</small>
                    </div>
                    <div className="ms-auto">
                        <span className={`badge fs-6 px-3 py-2 ${informacion?.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                            <i className={`fas ${informacion?.status === true ? 'fa-check-circle' : 'fa-clock'} me-1`}></i>
                            {informacion?.status === true ? 'Finalizada' : 'Pendiente'}
                        </span>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-dollar-sign text-success"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Total de la venta</div>
                            <div className="fw-bold fs-5">{informacion?.total != null ? formatNumber(informacion.total, 2, '$', '') : '-'}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-ice-cream text-info"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Total conos</div>
                            <div className="fw-bold fs-5">{informacion?.total_conos ?? '-'}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-hand-holding-usd text-warning"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Total comisión</div>
                            <div className="fw-bold fs-5">{informacion?.total_comision != null ? formatNumber(informacion.total_comision, 2, '$', '') : '-'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Información general */}
            <div className="col-12 mb-3">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-info-circle text-primary me-2"></i>
                            Información general
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Folio</div>
                                    <div className="fw-semibold">{informacion?.folio || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Fecha</div>
                                    <div className="fw-semibold">{formatFecha(informacion?.fecha)}</div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border-start border-secondary border-3 ps-3">
                                    <div className="text-muted small mb-1">Sucursal</div>
                                    <div className="fw-semibold">{informacion?.sucursal?.nombre || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border-start border-success border-3 ps-3">
                                    <div className="text-muted small mb-1">Vendedor</div>
                                    <div className="fw-semibold">{formatNombre(informacion?.vendedor)}</div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border-start border-warning border-3 ps-3">
                                    <div className="text-muted small mb-1">Responsable</div>
                                    <div className="fw-semibold">{formatNombre(informacion?.responsable)}</div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border-start border-info border-3 ps-3">
                                    <div className="text-muted small mb-1">Evento</div>
                                    <div className="fw-semibold">{informacion?.evento?.nombre || 'Sin evento'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Productos */}
            <div className="col-12">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-box text-primary me-2"></i>
                            Productos
                        </h6>
                        {informacion?.productos?.length > 0 && (
                            <span className="badge bg-primary rounded-pill">{informacion.productos.length}</span>
                        )}
                    </div>
                    <div className="card-body p-0">
                        <DataTable
                            {...dataTableProps}
                            columns={columnsProductos}
                            data={informacion?.productos || []}
                            noDataComponent={
                                <div className="py-5 text-center text-muted">
                                    <i className="fas fa-box fa-2x mb-2 d-block opacity-25"></i>
                                    No hay productos registrados en esta venta ambulante
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Empleados involucrados */}
            <div className="col-12 mb-3">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-users text-warning me-2"></i>
                            Empleados involucrados
                        </h6>
                        {informacion?.empleados?.length > 0 && (
                            <span className="badge bg-warning rounded-pill">{informacion.empleados.length}</span>
                        )}
                    </div>
                    <div className="card-body p-0">
                        <DataTable
                            {...dataTableProps}
                            columns={columnsEmpleados}
                            data={informacion?.empleados || []}
                            noDataComponent={
                                <div className="py-5 text-center text-muted">
                                    <i className="fas fa-users fa-2x mb-2 d-block opacity-25"></i>
                                    No hay empleados registrados en esta venta ambulante
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default InformacionVuelta;
